import { FC, MouseEvent, ReactNode } from "react";
import { clsx } from "clsx";
import { Link } from "react-router-dom";
import {
    AnalysisType,
    canDo,
    getAnalysisLabel,
    ProjectAction,
    ProjectAnalysisStatus,
    ProjectSummary,
} from "../../../types/ProjectTypes";
import classes from "./AnalysisProgressCell.module.less";

interface NextStep {
    label: string;
    /** Where the label leads: the intake while drafted, the report once ready. */
    path?: string;
    /** The step is on the user because the analyst sent the intake back. */
    warning?: boolean;
}

/**
 * What comes next for the latest analysis: back to the intake while it is a
 * draft, or, flagged, when the analyst asked for changes; waiting while it
 * is being analyzed; the report once it is ready. Nothing once the project
 * is closed.
 */
const nextStep = (project: ProjectSummary): NextStep | undefined => {
    const latest = project.latestAnalysis;
    // The intake steps are on the editors; everyone else only reads them.
    const intakePath = canDo(project, ProjectAction.EDIT) ? `/project/${project.id}` : undefined;
    switch (latest?.status) {
        case ProjectAnalysisStatus.REQUIRE_USER_ACTION:
            return { label: "Update intake", path: intakePath, warning: true };
        case ProjectAnalysisStatus.SUBMITTED:
            return { label: "Analyzing..." };
        case ProjectAnalysisStatus.COMPLETED:
            if (latest.type === AnalysisType.COMPLETE) return undefined;
            return {
                label: "Ready to review",
                path: `/project/${project.id}/analysis/${latest.version}`,
            };
        default:
            return { label: "Continue intake", path: intakePath };
    }
};

// The card around the cell navigates on click; a link must not also trigger it.
const stopPropagation = (e: MouseEvent) => e.stopPropagation();

/**
 * The latest analysis as a cell: its label on top ("Draft" before the first
 * submission) and the next step below, linking onward where there is
 * somewhere to go. A closed project with no report (completed RFx, cancelled
 * deal, deal signed before savings reports existed) has nothing to show here
 * and reads as a dash; once a report is published it reads "Savings Report".
 */
export const AnalysisProgressCell: FC<{ project: ProjectSummary }> = ({ project }) => {
    if (project.latestAnalysis?.type === AnalysisType.COMPLETE) return <>–</>;
    const name = project.latestAnalysis ? getAnalysisLabel(project.latestAnalysis) : "Draft";
    const next = nextStep(project);
    let step: ReactNode = null;
    if (next) {
        const className = clsx(classes.step, next.warning && classes["step--warning"]);
        step = next.path ? (
            <Link to={next.path} className={className} onClick={stopPropagation}>
                {next.label}
            </Link>
        ) : (
            <span className={className}>{next.label}</span>
        );
    }
    return (
        <div className={classes.cell}>
            <span className={classes.name}>{name}</span>
            {step}
        </div>
    );
};
